import { alpha, createTheme, Shadows, Theme } from "@mui/material/styles";
import { BO_CORE_DEFAULT_VALUE } from "./AppUtils";

export type ColorModeName = "light" | "dark";

// Exported for module-level contexts that cannot use useTheme (chart configs, status maps in
// plain utils). Components should keep reading the live theme via useTheme/styled/sx.
// Palette values follow the 1health design system (Graphite/Charcoal/Slate/Mist/Cloud neutrals,
// Trust Blue / Network Teal / Care Mint / Signal Aqua / Clinical Ice accents, Pulse/Coral/Amber
// action colors). Token NAMES are legacy (teal, merigold, cobaltBlue, ...) — they are consumed
// across ~200 files; only the values were re-pointed at the style guide.
export const defaultPalette = {
    // Widened to ColorModeName (not `as const`) so darkPalette — typed as `typeof defaultPalette` —
    // can hold "dark" without a cast, and so `palette.mode === "dark"` narrows honestly downstream.
    mode: "light" as ColorModeName,
    type: "white",
    primary: {
        main: "#246B8F", // Trust Blue
        secondary: "#5FC7B2", // Care Mint (mirrors teal.hover)
        contrastText: "#fff",
        softBlack: "#313B47", // Charcoal
    },
    secondary: {
        main: "#D6A83A", // Amber
        contrastText: "#000",
    },
    success: {
        main: "#2B7C58",
        light: "#E7F5EF", // pale success surface
        contrastText: "#fff",
    },
    error: {
        main: "#D54237", // Pulse, darkened for AA (error text on white + white on filled error)
        contrastText: "#fff",
    },
    warning: {
        main: "#D6A83A", // Amber
        contrastText: "#fff",
    },
    info: {
        main: "#2E7DA5",
        contrastText: "#fff",
    },
    text: {
        primary: "#313B47", // Charcoal — body text is neutral, teal is an accent only
        secondary: "#566373", // Slate
    },
    background: {
        paper: "#fff", // # the same
        default: "#fff", // # the same
    },
    action: {
        disabled: "rgba(77,77,77,0.9)",
        disabledBackground: "rgba(152,152,152,0.64)",
        active: "#8FD8E6", // Signal Aqua (mirrors teal.hoverOnDark)
    },
    teal: {
        main: "#1A8384", // Network Teal, darkened for AA text contrast (DS #1F9A9B is 3.4:1)
        hover: "#5FC7B2", // Care Mint
        hoverOnDark: "#8FD8E6", // Signal Aqua
        light: "#E8F7FA", // Clinical Ice
    },
    merigold: {
        main: "#D6A83A", // Amber — the only sanctioned yellow (semantic warning/attention); fills only
        hover: "#E2BF6B",
        light: "#F7F0DF",
        contrastText: "#313B47", // Charcoal
        text: "#89691D", // dark Amber for text/icons on light surfaces (main is 2.2:1 — never use it as text)
    },
    softBlack: {
        main: "#313B47", // Charcoal
        hover: "#566373", // Slate
    },
    mediumDarkGrey: {
        main: "#6B7787", // status Neutral, darkened for AA text contrast
    },
    silverGrey: {
        main: "#AEB8C4", // Mist
        light: "#E6EBF0", // Cloud
    },
    silver: {
        main: "#E6EBF0", // Cloud
    },
    softSilver: {
        main: "#F3F3F3",
        light: "#F8F8F8",
    },
    warningRed: {
        main: "#D54237", // Pulse, darkened for AA
        text: "#C92D20", // error/warning TEXT — passes 4.5:1 even on the pale rose tint (Coral #E8786F was 2.9:1)
        light: "#F8E7E7", // pale rose surface
    },
    approvedGreen: {
        main: "#2B7C58", // Success, darkened for AA contrast as chip text and under white icons
        text: "#287955", // darker success for labels on light/tinted surfaces
        light: "#6FC49A",
    },
    cobaltBlue: {
        main: "#246B8F", // Trust Blue
        hover: "#4A89A9",
        light: "#E7F0F5",
    },
    // Hyperlink color used by the AG Grid link/ID cell renderers.
    link: {
        main: "#2E7DA5", // Info family, 4.6:1 on white — AA for normal-size link text
    },
    // Near-black text ink (the app-wide de-facto body/heading color) + its alpha variants.
    ink: {
        main: "#202833", // Graphite
        soft: "rgba(32, 40, 51, 0.87)",
        border: "rgba(32, 40, 51, 0.12)",
    },
    // Radio/Checkbox selection controls (dark checked state paired with a neutral grey).
    control: {
        checked: "#313B47", // Charcoal
        unchecked: "#8796A8", // Mist darkened to 3:1 — WCAG minimum for form-control boundaries
    },
    // Neutral mid grey for secondary icons/disabled affordances.
    midGrey: {
        main: "#8796A8", // Mist darkened to 3:1 for interactive icons/affordances
    },
    overlay: {
        scrim: "rgba(32, 40, 51, 0.76)", // Graphite scrim
    },
    // Subtle slate border/shadow tint used on cards and panels.
    border: {
        subtle: "rgba(49, 59, 71, 0.08)", // Charcoal-based
        input: "#8796A8", // form-field boundaries — 3:1 on white (Cloud hairlines stay for decorative borders)
    },
    // Inverse surfaces: dark panels that contrast with the page in BOTH color modes
    // (sidebar, dark card headers). Deliberately does not flip to light in dark mode.
    surface: {
        inverse: "#313B47", // Charcoal
        inverseHover: "#566373", // Slate
    },
    // "pastel" design language (NG-8049 app launcher redesign) — warm neutrals + tinted tile pairs.
    // New launcher UI must consume these tokens instead of hardcoded hexes; values match the
    // approved design demo. Tint names (blue/teal/sage/...) are the design's canonical names.
    pastel: {
        canvas: "#FBFBFA",
        paper: "#FFFFFF",
        border: "rgba(26, 26, 24, 0.08)",
        ink: "#1A1A18",
        inkMuted: "#8A8A84",
        inkFaint: "#B3B3AD",
        tint: {
            blue: { bg: "#E3ECFA", fg: "#2F5FA8" },
            teal: { bg: "#DDF1EE", fg: "#1F7A6D" },
            sage: { bg: "#E4EFE2", fg: "#4A7A46" },
            amber: { bg: "#F5EDDC", fg: "#96712A" },
            rose: { bg: "#F8E7E7", fg: "#A94E4E" },
            lilac: { bg: "#ECE7F6", fg: "#6A54A3" },
            cyan: { bg: "#DFF0F6", fg: "#2A7490" },
            gray: { bg: "#ECECEA", fg: "#55554F" },
        },
    },
};

export type PastelTintName = keyof typeof defaultPalette.pastel.tint;

// Dark color scheme. Same semantic roles as defaultPalette; values derived from the design
// system's dark-brand direction (Graphite surfaces) with every text/control pairing verified
// against WCAG AA on the dark surfaces (see NG-8547). Accent hues match the light palette,
// lightened just enough to hold 4.5:1 (text) / 3:1 (controls) on Graphite paper. Notably the
// dark teal accent lands on the design guide's original Network Teal (#1F9A9B), which failed
// AA on white but passes on dark.
export const darkPalette: typeof defaultPalette = {
    // MUI computes its own internal defaults (divider, action.hover, grey ramp) from mode.
    mode: "dark",
    type: "dark",
    primary: {
        main: "#3294C6",
        secondary: "#5FC7B2",
        contrastText: "#0E1720",
        softBlack: "#DEE5EC",
    },
    secondary: {
        main: "#D6A83A",
        contrastText: "#000",
    },
    success: {
        main: "#369C6F",
        light: "#1B382C",
        contrastText: "#0E1720",
    },
    error: {
        main: "#DE6A61",
        contrastText: "#0E1720",
    },
    warning: {
        main: "#D6A83A",
        contrastText: "#0E1720",
    },
    info: {
        main: "#3796C6",
        contrastText: "#0E1720",
    },
    text: {
        primary: "#E6EBF0", // Cloud
        secondary: "#AEB8C4", // Mist
    },
    background: {
        paper: "#1D2530", // elevated Graphite
        default: "#141920", // deep Graphite canvas
    },
    action: {
        disabled: "rgba(230, 235, 240, 0.4)",
        disabledBackground: "rgba(230, 235, 240, 0.16)",
        active: "#8FD8E6",
    },
    teal: {
        main: "#1F9B9C", // ≈ the design guide's Network Teal — AA on dark surfaces
        hover: "#5FC7B2",
        hoverOnDark: "#8FD8E6",
        light: "#16333B", // dark Clinical Ice tint
    },
    merigold: {
        main: "#D6A83A", // Amber fills read well on dark (7.0:1)
        hover: "#E2BF6B",
        light: "#33291A", // dark amber tint
        contrastText: "#313B47",
        text: "#DBB86B", // amber text/icons on dark surfaces
    },
    softBlack: {
        main: "#DEE5EC", // text role inverts; inverse SURFACES use palette.surface instead
        hover: "#B9C3CE",
    },
    mediumDarkGrey: {
        main: "#808C9B",
    },
    silverGrey: {
        main: "#8A97A6",
        light: "#2A3440", // hairline borders on dark
    },
    silver: {
        main: "#2A3440",
    },
    softSilver: {
        main: "#232C37", // raised neutral surfaces
        light: "#1A212B",
    },
    warningRed: {
        main: "#DE6A61",
        // Lightened from #E5645A, which was only 4.33:1 on the warningRed.light tint below — the one
        // dark status pairing that missed AA. Now 4.9:1 on the tint, 5.2:1 on paper, 6.0:1 on canvas.
        text: "#EA7268",
        light: "#3A2422", // dark rose tint
    },
    approvedGreen: {
        main: "#369C6F",
        text: "#63BE92",
        light: "#1B382C",
    },
    cobaltBlue: {
        main: "#3294C6",
        hover: "#85B9DA",
        light: "#1E3242",
    },
    link: {
        main: "#3796C6",
    },
    ink: {
        main: "#E8EDF2",
        soft: "rgba(232, 237, 242, 0.87)",
        border: "rgba(232, 237, 242, 0.14)",
    },
    control: {
        checked: "#8FD8E6", // Signal Aqua pops as the checked accent on dark
        unchecked: "#8796A8",
    },
    midGrey: {
        main: "#8796A8",
    },
    overlay: {
        scrim: "rgba(8, 12, 18, 0.8)",
    },
    border: {
        subtle: "rgba(230, 235, 240, 0.08)",
        input: "#8796A8",
    },
    surface: {
        inverse: "#101820",
        inverseHover: "#1F2B38",
    },
    pastel: {
        canvas: "#12171E",
        paper: "#1A222B",
        border: "rgba(232, 234, 237, 0.08)",
        ink: "#E8EAED",
        inkMuted: "#9AA3AD",
        inkFaint: "#667080",
        tint: {
            blue: { bg: "#1E2B40", fg: "#9CC0F0" },
            teal: { bg: "#14333B", fg: "#7FD0C4" },
            sage: { bg: "#20301E", fg: "#A8CC9F" },
            amber: { bg: "#33291A", fg: "#DBB86B" },
            rose: { bg: "#3A2422", fg: "#E4A1A1" },
            lilac: { bg: "#292339", fg: "#C0AEE8" },
            cyan: { bg: "#16303C", fg: "#8FC6DE" },
            gray: { bg: "#262B31", fg: "#B9BEC6" },
        },
    },
};

/**
 * The active color mode, published synchronously by ColorModeProvider during its render.
 * <html data-color-mode> can't be the source of truth here: it is written from an effect, i.e.
 * AFTER the commit that already swapped the MUI theme, so anything reading the attribute during
 * that render saw the PREVIOUS mode — mixing one mode's surface with the other mode's ink until
 * the next refresh (the medical-database score badges rendered dark-on-dark that way).
 */
let activeColorMode: "light" | "dark" | undefined;

export const setActiveColorMode = (mode: "light" | "dark"): void => {
    activeColorMode = mode;
};

/**
 * The single resolver for "which color mode is active" outside React.
 *
 * Every non-React consumer MUST go through this rather than reading <html data-color-mode>
 * directly: the attribute is written from a layout effect, i.e. AFTER the commit that already
 * swapped the MUI theme, so a direct read disagrees with `activeColorMode` for exactly one render.
 * Two call sites in the same function disagreeing that way is what produced a dark-themed chart
 * with light-mode series hues.
 *
 * Falls back to the attribute for calls that happen before the provider first renders (the
 * index.html no-flash script sets it pre-paint), then to light in jsdom/tests where neither exists.
 */
export const getActiveColorMode = (): "light" | "dark" =>
    activeColorMode ?? (typeof document !== "undefined" && document.documentElement?.dataset?.colorMode === "dark" ? "dark" : "light");

/**
 * Palette matching the ACTIVE color mode, for module-level / plain-function contexts that
 * cannot use useTheme (chart configs, status color maps).
 * Must be called PER RENDER — capturing the result in a module-level constant reintroduces the
 * "stuck on light" bug. Components should keep reading the live theme via useTheme/styled/sx —
 * that is always consistent with the rendered mode.
 *
 * NOT suitable for AG Grid cell renderers or cellStyle callbacks: AG Grid does not re-run those
 * when the theme changes, so the resolved hex stays in the previous mode until the grid refreshes.
 * Use the --oneh-* CSS variables in src/index.css there instead — they re-resolve at paint time.
 */
export const getActivePalette = (): typeof defaultPalette => (getActiveColorMode() === "dark" ? darkPalette : defaultPalette);

// Silver is the light-mode watermark/muted icon fill, but its dark value is a surface tone that
// disappears on dark paper — icons keep silver in light and step up to the muted-icon grey in dark.
export const mutedIconFill = (theme: Theme): string => (theme.palette.mode === "dark" ? theme.palette.midGrey.main : theme.palette.silver.main);

// MUI applies `theme.shadows[elevation]` to every Paper-based surface, and Card, Dialog, Drawer,
// Menu, Popover and the Autocomplete dropdown are all Papers with different default elevations
// (Paper/Card 1, Menu/Popover 8, Drawer 16, Dialog 24). Overriding this one array therefore gives
// the whole app a consistent depth ramp without a single per-component rule.
//
// Each level is an ambient/key pair: a tight contact shadow plus a broad soft one. The design
// system's alphas (0.18-0.32, DS 5.3) are specified for cards on DARK surfaces - reused as-is in
// light mode they read as muddy grey halos - so the light ramp is scaled well down and dark steps
// back up ~4x, because dark surfaces swallow a soft shadow.
//
// Pure `rgba(0, 0, 0, a)` on purpose: the oneh/no-hardcoded-colors rule allows pure black for
// shadows, and a tinted near-black is imperceptible at these opacities. Deriving the tint from
// palette.ink.main would be wrong anyway - it is near-white in dark mode, i.e. a glow.
const buildShadows = (mode: ColorModeName): Shadows => {
    const isDark = mode === "dark";
    const near = isDark ? 0.16 : 0.04;
    const far = isDark ? 0.2 : 0.05;

    return Array.from({ length: 25 }, (_, level) => {
        if (level === 0) return "none";
        const t = level / 24;
        const nearY = Math.max(1, Math.round(1 + t * 11));
        const nearBlur = Math.max(2, Math.round(2 + t * 22));
        const farY = Math.round(2 + t * 30);
        const farBlur = Math.round(6 + t * 58);
        const nearAlpha = (near + t * near * 0.75).toFixed(3);
        const farAlpha = (far + t * far * 1.4).toFixed(3);
        return `0 ${nearY}px ${nearBlur}px rgba(0, 0, 0, ${nearAlpha}), 0 ${farY}px ${farBlur}px rgba(0, 0, 0, ${farAlpha})`;
    }) as unknown as Shadows;
};

const buildThemeConfig = (palette: typeof defaultPalette) => ({
    palette,
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1280,
            xl: 1536,
        },
    },
    typography: {
        fontFamily: "'Inter', sans-serif",
        allVariants: {
            color: palette.primary.softBlack,
        },
    },
    shadows: buildShadows(palette.mode),
    // Base radius for the small utility surfaces that inherit it (alerts, popovers, snackbars).
    // Cards/panels/modals step up to 16px and dropdowns are pinned to 8px in the components block.
    shape: { borderRadius: 8 },
    components: {
        // The app-wide surface treatment. Every <Paper sx={{ p: 2 }}> page panel (the de-facto page
        // surface across ~35 page trees), every Card and every Dialog is a Paper, so this single rule
        // is what separates a surface from the #f8f8f8 page canvas. The shadow itself arrives from the
        // shadows ramp above — only the radius, hairline and box model are set here.
        MuiPaper: {
            // Expressed through Paper's own class slots rather than an ownerState callback: these
            // theme configs are deep-cloned by consumers and tests (structuredClone chokes on
            // functions), so the config has to stay serializable.
            styleOverrides: {
                root: {
                    // No CssBaseline is mounted anywhere in this app, so elements default to
                    // content-box and any padded full-width surface would otherwise overflow.
                    boxSizing: "border-box" as const,
                },
                // The `rounded` slot is applied only when square={false} (the default), so squared
                // Papers keep their sharp corners for free.
                rounded: {
                    borderRadius: 16,
                },
                // `elevation` is the non-outlined variant — outlined already draws its own border.
                elevation: {
                    border: `1px solid ${palette.border.subtle}`,
                },
                // elevation={0} is used deliberately to keep a surface flat (nested cards, the app
                // bar), so it must not gain a hairline. Paper's overridesResolver emits this slot
                // after `elevation`, so it wins on order at the SAME one-class specificity — which
                // matters: a nested `&.MuiPaper-elevation0` would be two classes and would outrank
                // the component's own sx/styled border rather than merely defaulting it.
                elevation0: {
                    border: 0,
                },
            },
        },
        // AppBar is Paper-based, so the fixed full-width header would otherwise pick up 16px corners.
        // No `border: 0` here on purpose: the only AppBar in the app renders elevation={0} (so the
        // MuiPaper guard above already skips the hairline), and resetting the border from the theme
        // risks clobbering the borderBottom that StyledAppHeaderV2Bar draws itself.
        MuiAppBar: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                },
            },
        },
        // Drawers are edge-anchored — rounding all four corners detaches them from the viewport edge.
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRadius: 0,
                },
            },
        },
        // Dropdowns are small utility surfaces; the 16px card radius reads bubbly on them.
        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: 8,
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    borderRadius: 8,
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                paper: {
                    borderRadius: 8,
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    borderRadius: 6,
                },
            },
        },
        // Chrome logs "Blocked aria-hidden on an element because its descendant retained focus" when a
        // Dialog closes: MUI restores focus to the trigger button while the exit transition still holds
        // the modal mounted, so #root still carries aria-hidden at that moment. MUI's documented
        // workaround: close without waiting for the transition, so aria-hidden is removed before focus
        // returns. Trade-off: dialogs skip their fade-OUT (fade-in is unaffected).
        MuiDialog: {
            defaultProps: { closeAfterTransition: false },
        },
        // MUI's stock dark-mode Alert darkens the severity color by ~90%, producing a near-black
        // slab that over-contrasts against elevated surfaces (dialogs get lighter via the dark
        // elevation overlay). Use a translucent severity tint over the surface instead — the dark
        // counterpart of the soft light-mode band. Light mode keeps the MUI defaults.
        ...(palette.mode === "dark" && {
            MuiAlert: {
                styleOverrides: {
                    root: ({ ownerState }) => {
                        const severityColor = {
                            info: palette.info.main,
                            success: palette.success.main,
                            warning: palette.warning.main,
                            error: palette.error.main,
                        }[ownerState?.severity ?? "success"];
                        return (ownerState?.variant ?? "standard") === "standard" && severityColor ? { backgroundColor: alpha(severityColor, 0.16) } : {};
                    },
                },
            },
        }),
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: `${palette.control.unchecked} !important`,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    // DS radius-md. The CTA* variants below keep their 6.25rem pill on purpose —
                    // the pill is the brand's action shape, only the plain buttons are normalised.
                    borderRadius: `10px`,
                    fontSize: "1rem",
                },
            },
            variants: [
                {
                    props: { variant: "CTA" } as const,
                    style: {
                        padding: "0.375rem 1rem",
                        backgroundColor: palette.merigold.main,
                        // contrastText, not softBlack: the Amber fill stays Amber in dark mode, so its text must stay Charcoal
                        color: palette.merigold.contrastText,
                        borderRadius: "6.25rem",
                        border: 0,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.merigold.hover,
                            // fixed Charcoal — the hover fill is the same in both modes (also lifts light-mode contrast 3.5→4.7:1)
                            color: palette.merigold.contrastText,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTA-TEAL" } as const,
                    style: {
                        padding: "0.375rem 1rem",
                        backgroundColor: palette.teal.hoverOnDark,
                        // Signal Aqua fill is identical in both modes — fixed Charcoal text
                        color: palette.merigold.contrastText,
                        borderRadius: "6.25rem",
                        border: 0,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.teal.hover,
                            color: palette.merigold.contrastText,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTA-TEAL", disabled: true } as const,
                    style: {
                        padding: "0.375rem 1rem",
                        backgroundColor: palette.silver.main,
                        color: `${palette.silverGrey.main} !important`,
                        borderRadius: "6.25rem",
                        border: 0,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.silver.main,
                            color: `${palette.silverGrey.main} !important`,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTA", size: "small" } as const,
                    style: {
                        padding: "0.5rem 1rem",
                        backgroundColor: palette.merigold.main,
                        // contrastText, not softBlack: the Amber fill stays Amber in dark mode, so its text must stay Charcoal
                        color: palette.merigold.contrastText,
                        borderRadius: "6.25rem",
                        border: 0,
                        minHeight: "2.06rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.21,
                        fontWeight: 600,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.merigold.hover,
                            // fixed Charcoal — the hover fill is the same in both modes (also lifts light-mode contrast 3.5→4.7:1)
                            color: palette.merigold.contrastText,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTA", disabled: true } as const,
                    style: {
                        padding: "0.375rem 1rem",
                        backgroundColor: palette.silver.main,
                        color: `${palette.silverGrey.main} !important`,
                        borderRadius: "6.25rem",
                        border: 0,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.silver.main,
                            color: `${palette.silverGrey.main} !important`,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTA", size: "small", disabled: true } as const,
                    style: {
                        padding: "0.5rem 1rem",
                        backgroundColor: palette.silver.main,
                        color: `${palette.silverGrey.main} !important`,
                        borderRadius: "6.25rem",
                        border: 0,
                        minHeight: "2.06rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.21,
                        fontWeight: 600,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.silver.main,
                            color: `${palette.silverGrey.main} !important`,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTAGhost" } as const,
                    style: {
                        padding: "0.375rem 1rem",
                        backgroundColor: "transparent",
                        color: palette.primary.softBlack,
                        borderRadius: "6.25rem",
                        border: `1px solid ${palette.merigold.main}`,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.merigold.light,
                            color: palette.primary.softBlack,
                            borderRadius: "6.25rem",
                            border: `1px solid ${palette.merigold.main}`,
                        },
                    },
                },
                {
                    props: { variant: "CTAGhost", size: "small" } as const,
                    style: {
                        padding: "0.4375rem 1rem",
                        backgroundColor: "transparent",
                        color: palette.primary.softBlack,
                        borderRadius: "6.25rem",
                        border: `1px solid ${palette.merigold.main}`,
                        minHeight: "2.06rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.21,
                        fontWeight: 600,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.merigold.light,
                            color: palette.primary.softBlack,
                            borderRadius: "6.25rem",
                            border: `1px solid ${palette.merigold.main}`,
                        },
                    },
                },
                {
                    props: { variant: "CTAGhost", color: "primary" } as const,
                    style: {
                        padding: "0.375rem 1rem",
                        backgroundColor: "transparent",
                        color: palette.primary.softBlack,
                        borderRadius: "6.25rem",
                        border: `1px solid ${palette.teal.main}`,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.teal.light,
                            color: palette.primary.softBlack,
                            borderRadius: "6.25rem",
                            border: `1px solid ${palette.teal.main}`,
                        },
                    },
                },
                {
                    props: { variant: "CTAGhost", disabled: true } as const,
                    style: {
                        padding: "0.375rem 1rem",
                        backgroundColor: "transparent",
                        color: `${palette.silverGrey.main} !important`,
                        borderRadius: "6.25rem",
                        border: `1px solid ${palette.silver.main}`,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: "transparent !important",
                            color: `${palette.silverGrey.main} !important`,
                            borderRadius: "6.25rem",
                            border: `1px solid ${palette.silver.main}`,
                        },
                    },
                },
                {
                    props: { variant: "CTAGhost2" } as const,
                    style: {
                        padding: "0.375rem 1rem",
                        backgroundColor: "transparent",
                        color: `${palette.softBlack.main} !important`,
                        borderRadius: "6.25rem",
                        border: `1px solid ${palette.merigold.main}`,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        // No `color` override on hover: it would have to repeat the base rule's
                        // `!important` to take effect, and the value that used to sit here was
                        // merigold.light — the same color as the hover background, i.e. an invisible
                        // label the moment it won. The base ink stays correct on the tinted fill.
                        "&:hover": {
                            backgroundColor: palette.merigold.light,
                            borderRadius: "6.25rem",
                        },
                    },
                },

                {
                    props: { variant: "CTAGhost2", size: "small" } as const,
                    style: {
                        padding: "0.5rem 1rem",
                        backgroundColor: "transparent",
                        color: `${palette.softBlack.main} !important`,
                        borderRadius: "6.25rem",
                        border: `1px solid ${palette.merigold.main}`,
                        minHeight: "2.06rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.21,
                        fontWeight: 600,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.merigold.light,
                            color: palette.softBlack.hover,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTAGhost2", disabled: true } as const,
                    style: {
                        padding: "0.375rem 1rem",
                        backgroundColor: "transparent",
                        color: `${palette.softBlack.main} !important`,
                        borderRadius: "6.25rem",
                        border: `1px solid ${palette.silver.main}`,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.softSilver.light,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTAGhost2", size: "small", disabled: true } as const,
                    style: {
                        padding: "0.5rem 1rem",
                        backgroundColor: "transparent",
                        color: `${palette.softBlack.main} !important`,
                        borderRadius: "6.25rem",
                        border: `1px solid ${palette.silver.main}`,
                        minHeight: "2.06rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.21,
                        fontWeight: 600,
                        textAlign: "center" as const,
                        "&:hover": {
                            backgroundColor: palette.softSilver.light,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTAWITHICONLINK" } as const,
                    style: {
                        padding: "0.375rem 0",
                        backgroundColor: "transparent",
                        color: palette.primary.main,
                        border: 0,
                        minWidth: "auto",
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "left" as const,
                        justifyContent: "space-between",
                        "& .MuiButton-endIcon": {
                            width: "0.75rem",
                            height: "0.75rem",
                            marginLeft: "0.5rem",
                            color: "inherit",
                            "&>*:nth-of-type(1)": {
                                fontSize: "0.75rem",
                            },
                        },
                        "&:hover": {
                            color: palette.cobaltBlue.hover,
                            backgroundColor: "transparent",
                        },
                    },
                },
                {
                    props: { variant: "CTAIcon" } as const,
                    style: {
                        padding: "0.375rem 1.25rem",
                        backgroundColor: palette.merigold.main,
                        // contrastText, not softBlack: the Amber fill stays Amber in dark mode, so its text must stay Charcoal
                        color: palette.merigold.contrastText,
                        borderRadius: "6.25rem",
                        border: 0,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&.MuiButton-endIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginLeft: "0.5rem",
                        },
                        "&.MuiButton-startIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginRight: "0.5rem",
                        },
                        "&:hover": {
                            backgroundColor: palette.merigold.hover,
                            // fixed Charcoal — the hover fill is the same in both modes (also lifts light-mode contrast 3.5→4.7:1)
                            color: palette.merigold.contrastText,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTAIcon", disabled: true } as const,
                    style: {
                        padding: "0.375rem 1.25rem",
                        backgroundColor: palette.silver.main,
                        color: `${palette.silverGrey.main} !important`,
                        borderRadius: "6.25rem",
                        border: 0,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&.MuiButton-endIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginLeft: "0.5rem",
                        },
                        "&.MuiButton-startIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginRight: "0.5rem",
                        },
                        "&:hover": {
                            backgroundColor: palette.silver.main,
                            color: `${palette.silverGrey.main} !important`,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTAIcon", color: "primary" } as const,
                    style: {
                        padding: "0.375rem 1.25rem",
                        backgroundColor: palette.teal.main,
                        color: palette.primary.contrastText,
                        borderRadius: "6.25rem",
                        border: 0,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&.MuiButton-endIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginLeft: "0.5rem",
                        },
                        "&.MuiButton-startIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginRight: "0.5rem",
                        },
                        "&:hover": {
                            backgroundColor: palette.primary.secondary,
                            color: palette.primary.contrastText,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "CTAIcon", color: "primary", disabled: true } as const,
                    style: {
                        padding: "0.375rem 1.25rem",
                        backgroundColor: palette.silver.main,
                        color: `${palette.silverGrey.main} !important`,
                        borderRadius: "6.25rem",
                        border: 0,
                        minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        textAlign: "center" as const,
                        "&.MuiButton-endIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginLeft: "0.5rem",
                        },
                        "&.MuiButton-startIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginRight: "0.5rem",
                        },
                        "&:hover": {
                            backgroundColor: palette.silver.main,
                            color: `${palette.silverGrey.main} !important`,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "TextButtonWithIcon" } as const,
                    style: {
                        padding: "0",
                        color: palette.primary.softBlack,
                        backgroundColor: "transparent",
                        border: 0,
                        // minHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.21,
                        fontWeight: 600,
                        textAlign: "center" as const,
                        "& .MuiButton-startIcon": {
                            width: "1.25rem",
                            height: "1.25rem",
                            marginRight: "0.5rem",
                            color: "inherit",
                        },
                        "&:hover": {
                            color: palette.softBlack.hover,
                            backgroundColor: "transparent",
                        },
                    },
                },
                {
                    props: { variant: "TextButtonWithIcon", disabled: true } as const,
                    style: {
                        padding: "0",
                        color: palette.silverGrey.main,
                        border: 0,
                        fontSize: "0.875rem",
                        lineHeight: 1.21,
                        fontWeight: 600,
                        textAlign: "center" as const,
                        "& .MuiButton-startIcon": {
                            width: "1.25rem",
                            height: "1.25rem",
                            marginRight: "0.5rem",
                            color: "inherit",
                        },
                    },
                },
                {
                    props: { variant: "TextButton" } as const,
                    style: {
                        padding: ".2rem .9rem",
                        backgroundColor: palette.primary.contrastText,
                        color: palette.mediumDarkGrey.main,
                        border: `1px solid ${palette.silverGrey.light}`,
                        fontSize: "0.875rem",
                        lineHeight: 1.75,
                        fontWeight: 600,
                        textAlign: "center" as const,
                        "&:hover": {
                            color: palette.softBlack.hover,
                            backgroundColor: palette.silverGrey.light,
                        },
                    },
                },
                {
                    props: { variant: "TextButton", disabled: true } as const,
                    style: {
                        padding: ".2rem .9rem",
                        backgroundColor: palette.primary.contrastText,
                        color: palette.silver.main,
                        border: `1px solid ${palette.silverGrey.light}`,
                        fontSize: "0.875rem",
                        lineHeight: 1.21,
                        fontWeight: 600,
                        textAlign: "center" as const,
                    },
                },
                {
                    props: { variant: "ConfirmIconBtn" } as const,
                    style: {
                        padding: "0.375rem 0.95rem",
                        backgroundColor: palette.teal.light,
                        color: palette.softBlack.main,
                        border: 0,
                        maxHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.6,
                        fontWeight: 700,
                        width: "fitContent",
                        textAlign: "left" as const,
                        justifyContent: "space-between",
                        "& .MuiButton-endIcon": {
                            color: palette.teal.main,
                            fontSize: "1.25rem",
                            marginLeft: "0.5rem",
                        },
                        "& .MuiButton-startIcon": {
                            color: palette.teal.main,
                            fontSize: "1.25rem",
                            marginRight: "0.5rem",
                        },
                        "&:hover": {
                            backgroundColor: palette.teal.light,
                            color: palette.teal.main,
                        },
                    },
                },
                {
                    props: { variant: "ConfirmIconBtn", disabled: true } as const,
                    style: {
                        padding: "0.375rem 0.95rem",
                        backgroundColor: palette.silver.main,
                        color: palette.softBlack.main,
                        border: 0,
                        maxHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.6,
                        fontWeight: 700,
                        width: "fitContent",
                        textAlign: "left" as const,
                        justifyContent: "space-between",
                        "& .MuiButton-endIcon": {
                            color: palette.mediumDarkGrey.main,
                            fontSize: "1.25rem",
                            marginLeft: "0.5rem",
                        },
                        "& .MuiButton-startIcon": {
                            color: palette.warningRed.main,
                            fontSize: "1.25rem",
                            marginRight: "0.5rem",
                        },
                        "&:hover": {
                            backgroundColor: palette.silverGrey.light,
                            color: palette.softBlack.main,
                        },
                    },
                },
                {
                    props: { variant: "RejectIconBtn" } as const,
                    style: {
                        padding: "0.375rem 0.95rem",
                        backgroundColor: palette.warningRed.light,
                        color: palette.softBlack.main,
                        border: 0,
                        maxHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.6,
                        fontWeight: 700,
                        width: "fitContent",
                        textAlign: "left" as const,
                        justifyContent: "space-between",
                        "& .MuiButton-endIcon": {
                            color: palette.warningRed.main,
                            fontSize: "1.25rem",
                            marginLeft: "0.5rem",
                        },
                        "& .MuiButton-startIcon": {
                            color: palette.warningRed.main,
                            fontSize: "1.25rem",
                            marginRight: "0.5rem",
                        },
                        "&:hover": {
                            backgroundColor: palette.warningRed.light,
                            color: palette.warningRed.main,
                        },
                    },
                },
                {
                    props: { variant: "RejectIconBtn", disabled: true } as const,
                    style: {
                        padding: "0.375rem 0.95rem",
                        backgroundColor: palette.silver.main,
                        color: palette.softBlack.main,
                        border: 0,
                        maxHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.6,
                        fontWeight: 700,
                        width: "fitContent",
                        textAlign: "left" as const,
                        justifyContent: "space-between",
                        "& .MuiButton-endIcon": {
                            color: palette.mediumDarkGrey.main,
                            fontSize: "1.25rem",
                            marginLeft: "0.5rem",
                        },
                        "& .MuiButton-startIcon": {
                            color: palette.warningRed.main,
                            fontSize: "1.25rem",
                            marginRight: "0.5rem",
                        },
                        "&:hover": {
                            backgroundColor: palette.silverGrey.light,
                            color: palette.softBlack.main,
                        },
                    },
                },
                {
                    props: { variant: "PrimaryIcon" } as const,
                    style: {
                        padding: "0.375rem 1.25rem",
                        backgroundColor: palette.merigold.main,
                        // contrastText, not softBlack: the Amber fill stays Amber in dark mode, so its text must stay Charcoal
                        color: palette.merigold.contrastText,
                        borderRadius: "6.25rem",
                        border: 0,
                        maxHeight: "3rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.6,
                        fontWeight: 700,
                        width: "fitContent",
                        textAlign: "center" as const,
                        "&.MuiButton-endIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginLeft: "0.5rem",
                        },
                        "&.MuiButton-startIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginRight: "0.5rem",
                        },
                        "&:hover": {
                            backgroundColor: palette.merigold.hover,
                            // fixed Charcoal — the hover fill is the same in both modes (also lifts light-mode contrast 3.5→4.7:1)
                            color: palette.merigold.contrastText,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "PrimaryIcon", disabled: true } as const,
                    style: {
                        padding: "0.375rem 1.25rem",
                        backgroundColor: palette.silver.main,
                        color: palette.silverGrey.main,
                        borderRadius: "6.25rem",
                        border: 0,
                        maxHeight: "2rem",
                        fontSize: "0.875rem",
                        lineHeight: 1.28,
                        fontWeight: 700,
                        width: "fitContent",
                        textAlign: "center" as const,
                        "&.MuiButton-endIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginLeft: "0.5rem",
                        },
                        "&.MuiButton-startIcon": {
                            color: "inherit",
                            fontSize: "1.25rem",
                            marginRight: "0.5rem",
                        },
                        "&:hover": {
                            backgroundColor: palette.primary.secondary,
                            color: palette.primary.contrastText,
                            borderRadius: "6.25rem",
                        },
                    },
                },
                {
                    props: { variant: "BadgeButton" } as const,
                    style: {
                        padding: ".2rem .9rem",
                        color: palette.teal.main,
                        backgroundColor: palette.primary.contrastText,
                        border: `1px solid ${palette.teal.main}`,
                        fontSize: "0.875rem",
                        lineHeight: 1.75,
                        fontWeight: 600,
                        textAlign: "center" as const,
                        "& .MuiButton-startIcon": {
                            width: "1.25rem",
                            height: "1.25rem",
                            marginRight: "0.5rem",
                            color: "inherit",
                        },
                        "&:hover": {
                            color: palette.teal.hover,
                            backgroundColor: palette.teal.light,
                        },
                    },
                },
                {
                    props: { variant: "BadgeButton", disabled: true } as const,
                    style: {
                        padding: ".2rem .9rem",
                        backgroundColor: palette.silver.main,
                        color: palette.softBlack.main,
                        border: `1px solid ${palette.silverGrey.light}`,
                        fontSize: "0.875rem",
                        lineHeight: 1.75,
                        fontWeight: 600,
                        textAlign: "center" as const,
                        "& .MuiButton-startIcon": {
                            width: "1.25rem",
                            height: "1.25rem",
                            marginRight: "0.5rem",
                            color: "inherit",
                        },
                    },
                },
            ],
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    overflow: "hidden",
                    // DS 8.3 puts inputs at 10px — same as the buttons they sit beside. (The generic
                    // radius-sm/6px token in DS 5.2 covers tags and small UI, not form fields.)
                    borderRadius: `10px !important`,
                    color: palette.text.primary,
                    // Resting field boundary at 3:1 (WCAG 1.4.11) — MUI's default rgba(0,0,0,0.23) is ~1.9:1.
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: palette.border.input,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderWidth: "1px !important",
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginBottom: 0,
                    marginTop: 9,
                    marginRight: 0,
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: "0rem",
                    textAlign: "end" as const,
                },
            },
        },
    },
    props: {
        MuiOutlinedInput: {
            disableUnderline: true,
        },
    },
    brand: "#D54237", // Pulse (AA-darkened, matches error.main)
    neutral: "#AEB8C4", // Mist
    infoMissingText: palette.primary.softBlack,
    footerHeight: 72,
    mobileFooterHeight: 56,
});

export const defaultThemeConfig = buildThemeConfig(defaultPalette);
export const darkThemeConfig = buildThemeConfig(darkPalette);

export const isColorValid = (strColor: string): boolean => {
    const s = new Option().style;
    s.color = strColor;
    return s.color !== "";
};

const fillInvalidColors = (target: any, defaults: any, fields: string[]) => {
    fields.forEach((field) => {
        if (!isColorValid(target[field])) {
            // eslint-disable-next-line no-param-reassign
            target[field] = defaults && field in defaults ? defaults[field] : undefined;
        }
    });
};

const PALETTE_DEFAULT_FIELDS: Record<string, string[]> = {
    primary: ["main", "contrastText"],
    secondary: ["main", "contrastText"],
    text: ["primary", "secondary"],
    background: ["paper", "default"],
    action: ["disabled", "disabledBackground", "active"],
};

const applyPaletteDefaults = (paletteConfig: any, key: string) => {
    const fields = PALETTE_DEFAULT_FIELDS[key];
    if (!fields) return;
    fillInvalidColors(paletteConfig[key], (defaultThemeConfig.palette as any)[key], fields);
};

const applyTypographyDefaults = (typographyConfig: any, key: string) => {
    if (key === "allVariants") {
        if (!isColorValid(typographyConfig[key].color)) {
            // eslint-disable-next-line no-param-reassign
            typographyConfig[key].color =
                "allVariants" in defaultThemeConfig.typography && "color" in defaultThemeConfig.typography.allVariants
                    ? defaultThemeConfig.typography.allVariants.color
                    : undefined;
        }
        return;
    }
    if (key === "fontFamily" && (typographyConfig[key] === "" || typographyConfig[key] === BO_CORE_DEFAULT_VALUE)) {
        // eslint-disable-next-line no-param-reassign
        typographyConfig[key] = "fontFamily" in defaultThemeConfig.typography ? defaultThemeConfig.typography.fontFamily : undefined;
    }
};

export const generateTheme = (themeConfig: any) => {
    const shallowThemeConfig = themeConfig;

    // Backfills and the baked component colors below follow the mode the CONFIG declares, not the
    // light palette unconditionally: a dark tenant config previously got light custom groups and a
    // hardcoded black input ink, i.e. black text on a Graphite field.
    // NOTE: this theme is currently only used as a startup gate in App.tsx (see `customTheme`) and is
    // never handed to a ThemeProvider — keep it mode-correct so re-wiring tenant theming is safe.
    const basePalette = shallowThemeConfig?.palette?.mode === "dark" ? darkPalette : defaultPalette;

    // Tenant muiTheme configs from the backend only carry the standard MUI palette slots —
    // the custom groups below are not tenant-customizable, so always fill missing ones from
    // the defaults. Components rely on these being present (they are required on the Palette type).
    const customPaletteGroups = [
        "teal",
        "merigold",
        "softBlack",
        "mediumDarkGrey",
        "silverGrey",
        "silver",
        "softSilver",
        "warningRed",
        "approvedGreen",
        "cobaltBlue",
        "link",
        "ink",
        "control",
        "midGrey",
        "overlay",
        "border",
        "surface",
        "pastel",
    ] as const;
    customPaletteGroups.forEach((group) => {
        if (!shallowThemeConfig.palette[group]) {
            shallowThemeConfig.palette[group] = (basePalette as any)[group];
        }
    });

    Object.keys(themeConfig.palette).forEach((key) => applyPaletteDefaults(shallowThemeConfig.palette, key));
    Object.keys(shallowThemeConfig.typography).forEach((key) => applyTypographyDefaults(shallowThemeConfig.typography, key));

    const finalThemeConfig = {
        ...shallowThemeConfig,
        components: {
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        color: basePalette.text.primary,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: `5px !important`,
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        overflow: "hidden",
                        borderRadius: `8px !important`,
                        // Was a hardcoded #000 — unreadable on a dark field. text.primary is the
                        // near-black ink in light mode and Cloud in dark.
                        color: basePalette.text.primary,
                    },
                },
            },
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        marginBottom: 0,
                        marginTop: 9,
                    },
                },
            },
            props: {
                MuiOutlinedInput: { disableUnderline: true },
            },
        },
    };

    return createTheme(finalThemeConfig);
};
